import { useEffect } from 'react';
import { X } from 'lucide-react';

/**
 * Composant Modal réutilisable avec fermeture ESC + click extérieur
 * Conforme à la demande utilisateur : fermeture par ESC ou click extérieur
 */
const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  showCloseButton = true,
  closeOnBackdrop = true,
  maxWidth = 'max-w-2xl',
  className = ''
}) => {
  // Gestion de la touche ESC
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    // Empêcher le scroll du body quand la modale est ouverte
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    // Fermer seulement si on clique sur le backdrop, pas sur le contenu de la modale
    if (closeOnBackdrop && e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      onClick={handleBackdropClick}
    >
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />

        {/* Modal */}
        <div 
          className={`inline-block w-full ${maxWidth} my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl ${className}`}
          onClick={(e) => e.stopPropagation()} // Empêcher la fermeture quand on clique dans la modale
        >
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700">
              {title && (
                <h3 className="text-xl font-semibold text-white" id="modal-title">
                  {title}
                </h3>
              )}
              
              {showCloseButton && (
                <button 
                  onClick={onClose}
                  className="p-2 text-white hover:bg-white/20 rounded-lg transition"
                  aria-label="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className="px-6 py-6">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;
